/**
 * main.js
 *
 * Bootstraps Vue application for Vitrina Salon public showcase
 */

import { createApp } from 'vue';
import pinia, { initializeStores } from '@/js/stores';
import { resolveSalonId } from '@/js/utils/salonBootstrap.js';
import { load_salon_configs } from '@/js/apis/vitrinaConfig.js';

// Components
import App from './App.vue';

// Resolve the active salon id at boot: the vitrina-gae edge injects it into
// <meta name="salon-id"> from domain + slug; on localhost we fall back to the
// 'localhost-salon-id' localStorage override (see salonBootstrap.js). Exposed
// globally as the single source until the router/store consume it (#16).
window.__SALON_ID__ = resolveSalonId();

// TEMP probe: run the two-tier config load (injected/manifest hashes → IDB
// diff → selective blob fetch) and log timing + what was reused vs fetched.
// Remove once the store consumes it (#16). Also exposed as
// window.__loadConfig() to re-run from the console — a second run should
// report everything `reused` (zero blob fetches).
window.__loadConfig = async (salonId = window.__SALON_ID__) => {
  if (!salonId) return console.warn('[vitrina] no salon-id to load config for');
  const t0 = performance.now();
  try {
    const bundle = await load_salon_configs(salonId);
    const ms = Math.round(performance.now() - t0);
    const { source, reused, fetched, failed } = bundle.meta;
    console.info(
      `[vitrina] config for ${salonId} in ${ms}ms — source=${source}` +
        ` reused=[${reused}] fetched=[${fetched}]` +
        (failed.length ? ` FAILED=[${failed}]` : '')
    );
    return bundle;
  } catch (e) {
    console.error('[vitrina] config load failed:', e);
  }
};
if (window.__SALON_ID__) window.__loadConfig();

// Provide a global $alert stub so axios interceptors don't crash
window.$alert = window.$alert || function(opts) {
  console.log('[alert]', opts?.type || 'info', opts?.title || '', opts?.body || '');
};

const app = createApp(App);

// Install Pinia first
app.use(pinia);
// Initialize stores after Pinia is configured
initializeStores();

// i18n
import { i18n, determineUserLocale } from '@/js/locales';
// Register plugins: vuetify, axios, router
import { registerPlugins } from '@/js/plugins';
registerPlugins(app);

// Set the locale for i18n
const userLocale = determineUserLocale();
i18n.global.locale.value = userLocale;

// Use i18n in the app
app.use(i18n);
app.config.globalProperties.$t = i18n.global.t;
window["$t"] = i18n.global.t;

// Make the app globally accessible for debugging
window.$app = app;

// Mount the app
app.mount('#app');
